import {
    AlertCircle,
    CheckCircle,
    InfoCircle,
    X,
    AlertTriangle,
} from '@untitledui/icons';
import { motion, AnimatePresence } from 'motion/react';
import type { Toast } from 'react-hot-toast';
import { toast } from 'react-hot-toast';
import { cx } from 'src/lib/utils/cx';

type Variant = 'success' | 'error' | 'warning' | 'info';

const VARIANTS: Record<Variant, {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconClass: string;
    iconBg: string;
    bg: string;
    border: string;
    title: string;
}> = {
    success: {
        icon: CheckCircle,
        iconClass: 'text-green-600',
        iconBg: 'bg-green-100',
        bg: 'bg-green-50',
        border: 'border-green-200',
        title: 'text-green-800',
    },
    error: {
        icon: AlertCircle,
        iconClass: 'text-red-600',
        iconBg: 'bg-red-100',
        bg: 'bg-red-50',
        border: 'border-error_subtle',
        title: 'text-red-800',
    },
    warning: {
        icon: AlertTriangle,
        iconClass: 'text-yellow-600',
        iconBg: 'bg-yellow-100',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        title: 'text-yellow-800',
    },
    info: {
        icon: InfoCircle,
        iconClass: 'text-blue-600',
        iconBg: 'bg-blue-100',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        title: 'text-blue-800',
    },
};

function resolveVariant(t: Toast): Variant {
    if (t.type === 'success') return 'success';
    if (t.type === 'error') return 'error';
    return 'info';
}

interface UiToastProps {
    t: Toast;
}

export function UiToast({ t }: UiToastProps) {
    const variant = resolveVariant(t);
    const v = VARIANTS[variant];
    const Icon = v.icon;
    const message = typeof t.message === 'function' ? t.message(t) : t.message;

    return (
        <AnimatePresence>
        <motion.div
            key={t.id}
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={t.visible ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 60, scale: 0.95 }}
            exit={{ opacity: 0, x: 60, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28, mass: 0.8 }}
            className={cx(
                'flex w-full max-w-sm items-start gap-3 rounded-xl border px-4 py-3.5 shadow-lg',
                v.bg,
                v.border,
            )}
            role="alert"
        >
            {/* Icon */}
            <div className={cx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', v.iconBg)}>
                <Icon className={cx('h-4 w-4', v.iconClass)} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pt-0.5">
                <p className={cx('text-sm font-semibold leading-snug', v.title)}>
                    {String(message)}
                </p>
            </div>

            {/* Close */}
            <button
                type="button"
                onClick={() => toast.dismiss(t.id)}
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-600"
                aria-label="Fechar"
            >
                <X className="h-4 w-4" />
            </button>
        </motion.div>
        </AnimatePresence>
    );
}
